// T-025 Phase 3 — wire QC tables to Realtime with tiered invalidation.
//
// Mirrors useDevelopmentsRealtime.js (Phase 2, commit 2110d88) byte-by-byte
// where applicable. Three tables subscribed independently because each routes
// to a different "narrow" invalidation key:
//
//   qc_reports → ['qc-report', payload.id]
//   qc_items   → ['qc-report', payload.report_id]
//   qc_issues  → ['qc-report', payload.report_id] AND ['qc-issues', payload.report_id]
//
// Tier A broad (invalidated on every batch, regardless of source table):
//   ['qc-reports']            — list grid (useQcReports.js:10)
//   ['qcPendingQueue']        — manager work queue (useQcReports.js:163)
//   ['myQcReports']           — inspector own-reports list, prefix-matches
//                               ['myQcReports', inspectorName] (useQcReports.js:176)
//   ['qcInspectorStats']      — Overview tab per-inspector breakdown (useQcReports.js:195)
//   ['qc-supplier-counts']    — Settings supplier counts (QcSettingsSuppliers.js:79)
//   ['qc-inspector-counts']   — Settings inspector counts (QcSettingsInspectors.js:55)
//   ['qc-group']              — round group (rework rounds), prefix-matches
//                               ['qc-group', groupId] (QcReportHub.js:25)
//
// Tier A narrow (per-row, derived from payload — see table mapping above):
//   ['qc-report', id]         — single report detail (useQcReports.js:28)
//   ['qc-issues', reportId]   — issues for a report (useQcIssues.js:8)
//
// Tier B (trailing 10s debounce, fires once per quiet period):
//   ['qc-analytics']          — heavy analytics RPC, prefix-matches
//                               ['qc-analytics', period, supplier, inspector]
//                               (useQcAnalytics.js:51)
//
// Out of scope (not on the 3 target tables — separate Phase tickets if ever
// needed):
//   ['qc-issue-library']                               (qc_issue_library — its ONE key since 2026-09-14)
//   ['qc-suppliers'], ['qc-suppliers-active']          (qc_suppliers)
//   ['qc-inspectors'], ['qc-inspectors-active']        (qc_inspectors)
//   ['qcRole'], ['qcRoleUsers']                        (user_portal_permissions)
//   ['myInspectorRecord', email]                       (qc_inspectors)
//
// Self-FK qc_reports.parent_report_id (rework rounds) is T-009 territory.
// Invalidating ['qc-group'] (prefix) on any qc_reports change is broad enough
// to cover both parent and child report changes for the round-group view.

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { useRealtimeSubscription } from './useRealtimeSubscription';

const TIER_A_BROAD_KEYS = [
  ['qc-reports'],
  ['qcPendingQueue'],
  ['myQcReports'],
  ['qcInspectorStats'],
  ['qc-supplier-counts'],
  ['qc-inspector-counts'],
  ['qc-group'],
];

const TIER_B_DEBOUNCE_MS = 10_000;

export function useQcRealtime({ enabled = true } = {}) {
  const queryClient = useQueryClient();
  const tierBTimerRef = useRef(null);

  // Tier B trailing-edge debounce. Resets on every event in a burst; fires
  // once 10s after the last event lands.
  const scheduleTierB = () => {
    const wasScheduled = tierBTimerRef.current !== null;
    if (tierBTimerRef.current) clearTimeout(tierBTimerRef.current);

    if (!wasScheduled) {
      Sentry.addBreadcrumb({
        category: 'realtime',
        level: 'info',
        message: 'qc.tier_b_scheduled',
      });
    }

    tierBTimerRef.current = setTimeout(() => {
      tierBTimerRef.current = null;
      Sentry.addBreadcrumb({
        category: 'realtime',
        level: 'info',
        message: 'qc.tier_b_fired',
      });
      try {
        queryClient.invalidateQueries({ queryKey: ['qc-analytics'] });
      } catch (err) {
        Sentry.captureException(err, {
          tags: { source: 'useQcRealtime.tierB' },
        });
      }
    }, TIER_B_DEBOUNCE_MS);
  };

  // Cleanup pending Tier B on unmount so navigating away doesn't fire stale
  // invalidation against a torn-down QueryClient.
  useEffect(() => {
    return () => {
      if (tierBTimerRef.current) {
        clearTimeout(tierBTimerRef.current);
        tierBTimerRef.current = null;
      }
    };
  }, []);

  // Shared Tier-A-broad pass + Tier B schedule, called once per batch by each
  // table's subscription handler.
  const invalidateBroadAndScheduleB = (table, events) => {
    const affectedIds = events
      .map(e => e.new?.id || e.old?.id)
      .filter(Boolean);

    Sentry.addBreadcrumb({
      category: 'realtime',
      level: 'info',
      message: `qc.${table}.tier_a_invalidated`,
      data: {
        batch_size: events.length,
        affected_ids: affectedIds.slice(0, 5),
      },
    });

    try {
      TIER_A_BROAD_KEYS.forEach(key => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    } catch (err) {
      Sentry.captureException(err, {
        tags: { source: `useQcRealtime.${table}.tierABroad` },
      });
    }

    scheduleTierB();
  };

  // qc_reports — narrow key uses payload.new.id || payload.old.id
  useRealtimeSubscription({
    table: 'qc_reports',
    event: '*',
    enabled,
    onPayload: (events) => {
      invalidateBroadAndScheduleB('qc_reports', events);
      try {
        events.forEach(e => {
          const id = e.new?.id || e.old?.id;
          if (id) {
            queryClient.invalidateQueries({ queryKey: ['qc-report', id] });
          }
        });
      } catch (err) {
        Sentry.captureException(err, {
          tags: { source: 'useQcRealtime.qc_reports.tierANarrow' },
        });
      }
    },
  });

  // qc_items — narrow key uses payload.new.report_id || payload.old.report_id
  useRealtimeSubscription({
    table: 'qc_items',
    event: '*',
    enabled,
    onPayload: (events) => {
      invalidateBroadAndScheduleB('qc_items', events);
      try {
        events.forEach(e => {
          const reportId = e.new?.report_id || e.old?.report_id;
          if (reportId) {
            queryClient.invalidateQueries({ queryKey: ['qc-report', reportId] });
          }
        });
      } catch (err) {
        Sentry.captureException(err, {
          tags: { source: 'useQcRealtime.qc_items.tierANarrow' },
        });
      }
    },
  });

  // qc_issues — narrow keys use payload.new.report_id || payload.old.report_id
  // and invalidate BOTH ['qc-report', reportId] and ['qc-issues', reportId],
  // mirroring the existing mutation pattern in useQcIssues.js:37-38.
  useRealtimeSubscription({
    table: 'qc_issues',
    event: '*',
    enabled,
    onPayload: (events) => {
      invalidateBroadAndScheduleB('qc_issues', events);
      try {
        events.forEach(e => {
          const reportId = e.new?.report_id || e.old?.report_id;
          if (reportId) {
            queryClient.invalidateQueries({ queryKey: ['qc-report', reportId] });
            queryClient.invalidateQueries({ queryKey: ['qc-issues', reportId] });
          }
        });
      } catch (err) {
        Sentry.captureException(err, {
          tags: { source: 'useQcRealtime.qc_issues.tierANarrow' },
        });
      }
    },
  });
}
